import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { FavoriteTeam, UserPreferences, Sport } from '@/types';

interface FavoritesState {
  favorites: FavoriteTeam[];
  preferences: UserPreferences;
  addFavorite: (team: FavoriteTeam) => void;
  removeFavorite: (teamId: string, sport: Sport) => void;
  isFavorite: (teamId: string, sport: Sport) => boolean;
  updatePreferences: (prefs: Partial<UserPreferences>) => void;
}

const DEFAULT_PREFS: UserPreferences = {
  favoriteTeams: [],
  notifyGameStart: true,
  notifyGoals: true,
  notifyYellowCards: true,
  notifyRedCards: true,
  notifySubstitutions: true,
  notifyHalftime: true,
  notifySecondHalf: true,
  notifyGameEnd: true,
  notifyScoreChange: true,
};

export const useFavoritesStore = create<FavoritesState>()(
  persist(
    (set, get) => ({
      favorites: [],
      preferences: DEFAULT_PREFS,

      addFavorite: (team) => {
        const already = get().isFavorite(team.id, team.sport);
        if (already) return;
        set((s) => ({ favorites: [...s.favorites, team] }));
      },

      removeFavorite: (teamId, sport) => {
        set((s) => ({
          favorites: s.favorites.filter((t) => !(t.id === teamId && t.sport === sport)),
        }));
      },

      isFavorite: (teamId, sport) => {
        return get().favorites.some((t) => t.id === teamId && t.sport === sport);
      },

      updatePreferences: (prefs) => {
        set((s) => ({ preferences: { ...s.preferences, ...prefs } }));
      },
    }),
    {
      name: 'sports-favorites',
      storage: createJSONStorage(() => AsyncStorage),
      // Garante que novos campos de preferência ganhem o valor padrão para
      // usuários que já tinham preferências salvas (sem esses campos)
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<FavoritesState>;
        return {
          ...current,
          ...p,
          preferences: { ...current.preferences, ...(p.preferences ?? {}) },
        };
      },
    }
  )
);
